import cn from 'classnames';
import React, { useState, useMemo, useCallback } from 'react';

import useTranslation from '../../../../hooks/useTranslation';

import './MonthView.scss';

const MAX_ROW = 5;
const MIN_ROW = 1;
const MAX_DAY = 31;
const MIN_DAY = 1;

const getDirectionOfFocus = (
    direction: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown',
    currentFocus: { row: number; day: number },
    maxDay: number,
    maxRow: number,
) => {
    switch (direction) {
        case 'ArrowUp':
            if (currentFocus.row !== maxRow && currentFocus.day - 7 < MIN_DAY) {
                return maxDay;
            }
            return currentFocus.row === MIN_ROW ? maxDay : currentFocus.day - 7;
        case 'ArrowDown':
            if (currentFocus.row !== maxRow && currentFocus.day + 7 > maxDay) {
                return maxDay;
            }
            return currentFocus.row === maxRow ? MIN_DAY : currentFocus.day + 7;
        case 'ArrowRight':
            return currentFocus.day === maxDay ? MIN_DAY : currentFocus.day + 1;
        case 'ArrowLeft':
            return currentFocus.day === MIN_DAY ? maxDay : currentFocus.day - 1;
        default:
            return MIN_DAY;
    }
};

type CalendarButtonProp = {
    day: number;
    row: number;
    isSelected: boolean;
    onSelect: (day: number) => void;
    wrapper: HTMLDivElement | null;
    maxDay: number;
    maxRow: number;
    box?: boolean;
    buttonText?: string;
    disabled: boolean;
};
const CalendarDateButton: React.FC<CalendarButtonProp> = ({
    day,
    row,
    isSelected,
    onSelect,
    wrapper,
    maxDay,
    maxRow,
    box = false,
    buttonText = '',
    disabled = false,
}) => {
    const style = cn('sgw-calendar__view-info', {
        '-box': box,
        '-cell': !box,
        '-selected': isSelected,
        '-disabled': disabled,
    });

    const attributes = {
        'aria-selected': isSelected,
    };

    const onKeydown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, row: number, day: number) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                onSelect(day);
                return;
            }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                const nextFocusTarget = getDirectionOfFocus(e.key, { row, day }, maxDay, maxRow);

                const element = wrapper?.querySelector(`#day-${nextFocusTarget}`);
                if (element) {
                    (element as HTMLElement).focus();
                }
            }
        },
        [maxDay, maxRow, wrapper, onSelect],
    );

    return (
        <button
            {...attributes}
            disabled={disabled}
            id={!box ? `day-${day}` : undefined}
            type="button"
            className={style}
            onClick={() => {
                onSelect(day);
            }}
            tabIndex={isSelected || box ? undefined : -1}
            onKeyDown={(e) => (box ? null : onKeydown(e, row, day))}
        >
            {box ? (
                <span>
                    <strong>{`${buttonText} ${day}.`}</strong>
                </span>
            ) : (
                <span>
                    <strong>{`${day}`}</strong>
                </span>
            )}
        </button>
    );
};

interface Props {
    selected: number | undefined;
    onSelect: (v: number) => void;
    className?: string;
    maxDay?: number;
    minDay: number;
}

const MonthView: React.FC<Props> = ({ selected, onSelect, className, maxDay, minDay }) => {
    const translated = useTranslation();
    const [wrapper, setWrapperRef] = useState<HTMLDivElement | null>(null);
    const monthArray = useMemo(() => {
        return Array.from({ length: 5 }, (_, i) => i + 1)
            .map((x) => {
                const day = x === 1 ? x - 1 : (x - 1) * 7;
                const column = Array.from({ length: 7 }, (_, i) => i + 1).map((index) => {
                    const dayInCol = day + index > (maxDay ?? MAX_DAY) ? null : day + index;
                    return dayInCol ?? 0;
                });
                return { row: x, list: column.filter((x) => x > 0) };
            })
            .filter((x) => x.list.length > 0);
    }, [maxDay]);
    const lastRow = monthArray[monthArray.length - 1]?.row;

    return (
        <div className={cn('sgw-calendar month-view', className)}>
            <div className="sgw-calendar__view-wrapper" ref={(el) => setWrapperRef(el)}>
                {wrapper && (
                    <ul>
                        {monthArray.map((month) =>
                            month.list
                                .filter((day) => day >= minDay)
                                .map((day) => (
                                    <li key={day}>
                                        <CalendarDateButton
                                            day={day}
                                            row={month.row}
                                            onSelect={onSelect}
                                            isSelected={day === selected}
                                            wrapper={wrapper}
                                            maxRow={lastRow ?? MAX_ROW}
                                            maxDay={maxDay ?? MAX_DAY}
                                            box
                                            buttonText={translated.loanEdit.dayPrefix}
                                            disabled={false}
                                        />
                                    </li>
                                )),
                        )}
                    </ul>
                )}

                <table role="grid">
                    <tbody>
                        {wrapper && (
                            <>
                                {monthArray.map((month) => (
                                    <tr key={month.row}>
                                        {month.list.map((day) => (
                                            <td key={day}>
                                                <CalendarDateButton
                                                    buttonText={translated.loanEdit.everyDay}
                                                    day={day}
                                                    row={month.row}
                                                    onSelect={onSelect}
                                                    isSelected={day === selected}
                                                    wrapper={wrapper}
                                                    maxRow={lastRow ?? MAX_ROW}
                                                    maxDay={maxDay ?? MAX_DAY}
                                                    disabled={day < minDay}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MonthView;
